/**
 * 🧚 GUIDE ASSISTANT - Pixie/Enzo accompagne chaque étape
 * 
 * Pixie (👧) - Guide pour les hommes (comme Max pour la livraison!)
 * Enzo (👨) - Guide pour les femmes
 * 
 * Messages contextuels et bienveillants à chaque étape
 * Mode Mozart/Jobs : Simplicité + Excellence ! 🎵🍎
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ChevronRight, Camera, Heart, MapPin, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';

// 🧚 PIXIE - Guide pour les hommes (comme Max!)
const PIXIE_GUIDE = {
  name: 'Pixie',
  emoji: '🧚',
  avatar: '👧',
  // 🖼️ Images réelles de Pixie générées par IA
  avatarImage: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/4a3ba43ac358122fdf7de47efdbaacb2bb71261f678c9c6e4499cfeb21ba4d0a.png',
  color: 'from-pink-500 to-purple-500',
  messages: {
    welcome: [
      "Hey beau gosse ! 🔥 Je suis Pixie, ta commandante à bord ! Prêt à trouver l'amour parmi les étoiles ?",
      "Salut toi ! 💜 Pixie à ton service... L'amour est au coin de la rue !",
      "Coucou ! ✨ Je suis trop contente de te voir ici ! Ensemble on va faire des étincelles !",
    ],
    photo: [
      "Allez, montre-moi ton plus beau sourire ! 📸 C'est ta première impression !",
      "Une vraie photo = 10x plus de matchs ! Fais-moi confiance 😏",
      "Fais péter ta plus belle tof ! Les filles adorent les sourires sincères 💜",
    ],
    identity: [
      "Dis-moi qui tu es ! Ton prénom, c'est le début de l'histoire... 💫",
      "Un pseudo mystérieux ? Ça intrigue toujours ! 😏",
    ],
    bio: [
      "100 caractères pour briller ! Fais-toi désirer... 🔥",
      "Ta bio c'est ton pitch ! Court, punchy, mémorable !",
    ],
    city: [
      "Tu habites où ? On va te trouver des gens sympas près de chez toi ! 📍",
      "Heartbeats c'est local ! Plus c'est proche, plus c'est chaud 🔥",
    ],
    preferences: [
      "Alors, tu recherches quoi comme rencontre ? 💜 Dis-moi tout...",
      "Tes préférences, c'est notre secret ! 😉",
    ],
    passions: [
      "Maintenant les choses sérieuses ! 🌌 Tes passions profondes...",
      "C'est ici qu'on trouve les âmes qui vibrent comme toi ! ✨",
      "Prends ton temps, c'est important pour le matching ! 💫",
    ],
    complete: [
      "YEEEES ! 🎉 Ton profil est canon ! Prêt à faire des ravages ?",
      "Bravo beau gosse ! 🔥 Tu vas les faire craquer !",
      "Parfait ! 💜 L'aventure commence maintenant...",
    ],
  }
};

// 👨 ENZO - Guide pour les femmes
const ENZO_GUIDE = {
  name: 'Enzo',
  emoji: '✨',
  avatar: '👨',
  // 🖼️ Images réelles d'Enzo générées par IA
  avatarImage: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/caa50018dd9bfa66fa4ce2dfe1b4c81b67f498df4d564cfe39bf15ee0fc367f2.png',
  color: 'from-blue-500 to-purple-500',
  messages: {
    welcome: [
      "Hey belle demoiselle ! 💜 Je suis Enzo, ton guide vers l'amour...",
      "Salut toi ! ✨ Enzo pour te servir... Prête pour l'aventure ?",
      "Waouh ! 🔥 Enfin tu es là ! Je t'attendais...",
    ],
    photo: [
      "Une photo qui te ressemble ! Sois toi-même, c'est ça qui attire 💜",
      "Montre ton plus beau sourire ! 📸 Tu vas faire craquer des cœurs !",
      "Vraie photo = vrais matchs ! Fais-moi confiance 😏",
    ],
    identity: [
      "Dis-moi ton prénom... C'est le début d'une belle histoire 💫",
      "Un pseudo mystérieux ? J'adore ! Ça donne envie d'en savoir plus 😉",
    ],
    bio: [
      "100 caractères pour te démarquer ! Fais-toi désirer... ✨",
      "Ta bio, c'est toi en mode teaser ! Court et mémorable !",
    ],
    city: [
      "Tu habites où ? 📍 On va te trouver des gens sympas près de chez toi !",
      "Plus c'est proche, plus c'est facile de se voir ! 😉",
    ],
    preferences: [
      "Alors, tu cherches quoi ? 💘 Dis-moi tout, je suis une tombe !",
      "Tes préférences restent entre nous... 🤫",
    ],
    passions: [
      "Maintenant, on passe aux choses sérieuses ! 🌌",
      "Tes passions profondes... C'est là que la magie opère ! ✨",
      "Prends ton temps, c'est important pour trouver ton match parfait !",
    ],
    complete: [
      "Magnifique ! 🎉 Ton profil est au top ! Les matchs arrivent...",
      "Bravo belle demoiselle ! 💜 Tu vas faire des ravages !",
      "Parfait ! ✨ L'aventure commence maintenant...",
    ],
  }
};

// Sélectionner le guide en fonction du genre
export const getGuideForGender = (gender) => {
  // Pixie pour les hommes, Enzo pour les femmes
  return gender === 'female' ? ENZO_GUIDE : PIXIE_GUIDE;
};

// Obtenir un message aléatoire pour un contexte donné
const getRandomMessage = (guide, context) => {
  const messages = guide.messages[context] || guide.messages.welcome;
  return messages[Math.floor(Math.random() * messages.length)];
};

// 🎨 Composant Guide Assistant
export const GuideAssistant = ({
  gender = 'male', // 'male' ou 'female'
  context = 'welcome', // 'welcome', 'photo', 'identity', 'bio', 'city', 'preferences', 'passions', 'complete'
  customerName = null,
  onDismiss,
  onAction,
  actionLabel = null,
  isDarkMode = true,
  minimized = false,
  position = 'floating', // 'floating', 'inline', 'banner'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(minimized);
  const [message, setMessage] = useState('');
  
  const guide = getGuideForGender(gender);
  
  useEffect(() => {
    // Générer un nouveau message à chaque changement de contexte
    let newMessage = getRandomMessage(guide, context);
    
    // Personnaliser avec le prénom si disponible
    if (customerName && customerName.length > 0) {
      // Remplacer uniquement les termes d'adresse, pas tous les "toi"
      newMessage = newMessage
        .replace(/beau gosse/gi, customerName)
        .replace(/belle demoiselle/gi, customerName)
        .replace(/Hey toi !/gi, `Hey ${customerName} !`)
        .replace(/Salut toi !/gi, `Salut ${customerName} !`)
        .replace(/Coucou !/gi, `Coucou ${customerName} !`)
        .replace(/Bravo !/gi, `Bravo ${customerName} !`);
    }
    
    setMessage(newMessage);
  }, [context, customerName, guide]);
  
  if (!isVisible) return null;
  
  // Mode Banner (en haut du formulaire)
  if (position === 'banner') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`mb-4 p-3 rounded-xl ${isDarkMode ? 'bg-gradient-to-r ' + guide.color + '/20' : 'bg-gradient-to-r ' + guide.color + '/10'} border ${isDarkMode ? 'border-purple-500/30' : 'border-purple-200'}`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${guide.color} flex items-center justify-center text-xl flex-shrink-0 overflow-hidden`}>
            {guide.avatarImage ? (
              <img src={guide.avatarImage} alt={guide.name} className="w-full h-full object-cover" />
            ) : (
              guide.avatar
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
              {guide.name} {guide.emoji}
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {message}
            </p>
          </div>
          {onDismiss && (
            <button
              onClick={() => { setIsVisible(false); onDismiss?.(); }}
              className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/10'}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {actionLabel && onAction && (
          <Button
            size="sm"
            onClick={onAction}
            className={`mt-2 w-full bg-gradient-to-r ${guide.color} text-white`}
          >
            {actionLabel} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </motion.div>
    );
  }
  
  // Mode Inline (dans le flux)
  if (position === 'inline') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-start gap-2 mb-3"
      >
        <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${guide.color} flex items-center justify-center text-lg flex-shrink-0 overflow-hidden`}>
          {guide.avatarImage ? (
            <img src={guide.avatarImage} alt={guide.name} className="w-full h-full object-cover" />
          ) : (
            guide.avatar
          )}
        </div>
        <div className={`flex-1 p-2 rounded-xl rounded-tl-none ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {message}
          </p>
        </div>
      </motion.div>
    );
  }
  
  // Mode Floating (bulle flottante)
  if (isMinimized) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsMinimized(false)}
        className={`fixed bottom-20 right-4 w-14 h-14 rounded-full bg-gradient-to-r ${guide.color} flex items-center justify-center text-2xl shadow-lg z-50 overflow-hidden`}
      >
        {guide.avatarImage ? (
          <img src={guide.avatarImage} alt={guide.name} className="w-full h-full object-cover" />
        ) : (
          guide.avatar
        )}
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 animate-pulse" />
      </motion.button>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      className={`fixed bottom-20 right-4 max-w-xs z-50`}
    >
      <div className={`rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        {/* Header */}
        <div className={`bg-gradient-to-r ${guide.color} p-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl overflow-hidden">
              {guide.avatarImage ? (
                <img src={guide.avatarImage} alt={guide.name} className="w-full h-full object-cover" />
              ) : (
                guide.avatar
              )}
            </div>
            <div>
              <p className="text-white font-bold text-sm">{guide.name}</p>
              <p className="text-white/70 text-xs">Ton guide 💜</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1 rounded-full hover:bg-white/20"
            >
              <span className="text-white text-lg">−</span>
            </button>
            {onDismiss && (
              <button
                onClick={() => { setIsVisible(false); onDismiss?.(); }}
                className="p-1 rounded-full hover:bg-white/20"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        </div>
        
        {/* Message */}
        <div className="p-4">
          <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {message}
          </p>
          
          {actionLabel && onAction && (
            <Button
              size="sm"
              onClick={onAction}
              className={`mt-3 w-full bg-gradient-to-r ${guide.color} text-white`}
            >
              {actionLabel} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default GuideAssistant;
